import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  Heading,
  MultiCombobox,
  PageHeading,
  Pagination,
  Panel,
  SearchInput,
  SectionHeader,
  SegmentedControl,
  SelectInput,
  Skeleton,
  SkeletonTable,
  StatTile,
  TrendChart,
  usePagedRows,
  type DataTableCard,
  type DataTableSort,
  type TrendOverlay,
  type TrendPoint,
  type TrendSeries,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { periodLabel } from '@/lib/report-window';
import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';
import {
  PPC_WINDOW_PARAM,
  parseSelection,
  ppcPeriodLabel,
  ppcWindow,
  todayEastern,
  windowLabel,
  type PickMode,
  type PpcPreset,
} from '@/lib/ppc-window';
import { PpcRangeControl } from '@/pages/ppc-range-control';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { InfoTip } from '@/components/metric-info';
import {
  PPC_COMPARE,
  PPC_COMPARES,
  PPC_ENTITY_KIND,
  PPC_METRIC,
  PPC_METRICS,
  PPC_SPLIT,
  PPC_SPLITS,
  readPpcAnalytics,
  type PpcAnalytics,
  type PpcCompare,
  type PpcEntityKind,
  type PpcMetricKey,
  type PpcMetrics,
  type PpcSplit,
} from '@/lib/ppc';
import { NO_VALUE, PPC_METRIC_DEFS, PPC_METRIC_GROUPS, changePct, formatMetric } from '@/lib/ppc-metrics';

/**
 * Analytics → PPC: every Google Ads number and what it turned into, over
 * time. One page: filters (range, interval, compare, campaigns, ad groups),
 * a tile per metric, a small chart per metric (one line per campaign or ad
 * group when split), and a table with every metric per campaign or group.
 * All aggregation happens in the API (GET /ads/ppc-analytics).
 */

// ─── URL state ──────────────────────────────────────────────────────────────

export const PPC_PARAM = {
  metric: 'metric',
  compare: 'compare',
  split: 'split',
  campaigns: 'campaigns',
  groups: 'groups',
} as const;
/** The table's own pager, so it never collides with another list's. */
const TABLE_PAGE_PARAM = 'page';
const LIST_SEPARATOR = ',';

const COMPARE_LABELS: Readonly<Record<PpcCompare, string>> = {
  none: 'No comparison',
  previous: 'Previous period',
  last_year: 'Same period last year',
};
const COMPARE_SHORT_LABELS: Readonly<Record<PpcCompare, string>> = { none: 'None', previous: 'Previous', last_year: 'Last year' };
const SPLIT_LABELS: Readonly<Record<PpcSplit, string>> = { none: 'Nothing', campaign: 'Campaign', group: 'Ad group' };
const ROW_KIND_LABELS: Readonly<Record<PpcEntityKind, string>> = {
  campaign: 'Campaign',
  ad_group: 'Ad group',
  asset_group: 'Performance Max asset group',
  campaign_no_group: 'Leads without an ad group (Performance Max)',
  unmatched: 'Click not in the click map',
  other: 'Other',
};

const isCompare = (value: string | null): value is PpcCompare => PPC_COMPARES.some((mode) => mode === value);
const isSplit = (value: string | null): value is PpcSplit => PPC_SPLITS.some((mode) => mode === value);
const listParam = (value: string | null): string[] => (value ?? '').split(LIST_SEPARATOR).filter((item) => item !== '');

// ─── Table ──────────────────────────────────────────────────────────────────

const TABLE_SORT_KEYS: readonly string[] = ['label', ...PPC_METRICS];
const DEFAULT_SORT: DataTableSort = { key: PPC_METRIC.spend, direction: 'desc' };
/** The metrics a phone card shows; the rest are a table column away. */
const CARD_METRICS: readonly PpcMetricKey[] = [
  PPC_METRIC.spend,
  PPC_METRIC.clicks,
  PPC_METRIC.cpc,
  PPC_METRIC.conversions,
  PPC_METRIC.leads,
  PPC_METRIC.costPerLead,
  PPC_METRIC.booked,
  PPC_METRIC.roas,
];
const SEARCH_FIELDS = ['label', 'campaignName'] as const;

// ─── Charts ─────────────────────────────────────────────────────────────────

const CHART_HEIGHT = 380;
/** Up to this many periods every one is labelled; past it the labels thin to about this many. */
const LABELLED_PERIODS = 13;
const DEFAULT_METRIC: PpcMetricKey = PPC_METRIC.spend;
const isMetric = (value: string | null): value is PpcMetricKey => PPC_METRICS.some((metric) => metric === value);
/** 0 labels every period; n labels every (n+1)th. */
const labelInterval = (points: number): number => Math.max(0, Math.ceil(points / LABELLED_PERIODS) - 1);
/** A period with nothing to divide by draws a gap, never a zero. */
const gap = (value: number | null): number => (value === null ? Number.NaN : value);

function chartPoints(data: PpcAnalytics, metric: PpcMetricKey): TrendPoint[] {
  const { interval } = data.range;
  // The axis carries the short date; the tooltip names the whole period ("Week of Oct 6").
  return data.periods.map((period, index) => ({
    key: period,
    label: periodLabel(period, interval),
    value: gap(data.series[index]?.[metric] ?? null),
    details: interval === STATS_INTERVAL.week ? [ppcPeriodLabel(period, interval)] : [],
  }));
}

function compareOverlay(data: PpcAnalytics, metric: PpcMetricKey): TrendOverlay[] {
  if (data.compare === null || data.compareSeries === null) return [];
  const series = data.compareSeries;
  return [{ key: 'compare', label: COMPARE_LABELS[data.compare.mode], tone: 1, values: data.periods.map((_, index) => series[index]?.[metric] ?? null) }];
}

function splitLines(data: PpcAnalytics, metric: PpcMetricKey): TrendSeries[] | undefined {
  if (data.splitSeries === null) return undefined;
  return data.splitSeries.map((line) => ({ key: line.key, label: line.label, values: line.series.map((point) => gap(point[metric])) }));
}

// ─── Page ───────────────────────────────────────────────────────────────────

/** Under a small day's traffic one lead more or less moves a rate a lot. */
const DAILY_RATES_NOTE = 'Daily rates on small days swing a lot; read weekly for trends.';

export function PpcAnalyticsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const selection = parseSelection(params);
  const today = todayEastern();
  const span = ppcWindow(selection, params.get(PPC_WINDOW_PARAM.interval));
  const rawCompare = params.get(PPC_PARAM.compare);
  // "All time" has nothing before it to compare with.
  const compare: PpcCompare = !span.compare ? PPC_COMPARE.none : isCompare(rawCompare) ? rawCompare : PPC_COMPARE.previous;
  const rawSplit = params.get(PPC_PARAM.split);
  const split: PpcSplit = isSplit(rawSplit) ? rawSplit : PPC_SPLIT.none;
  const campaignIds = listParam(params.get(PPC_PARAM.campaigns));
  const groupKeys = listParam(params.get(PPC_PARAM.groups));
  const rawMetric = params.get(PPC_PARAM.metric);
  const metric: PpcMetricKey = isMetric(rawMetric) ? rawMetric : DEFAULT_METRIC;

  /** Every filter change starts the table on its first page. */
  const update = (change: (next: URLSearchParams) => void): void =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        change(next);
        next.delete(TABLE_PAGE_PARAM);
        return next;
      },
      { replace: true },
    );
  const setParam = (key: string, value: string): void => update((next) => next.set(key, value));
  const setList = (key: string, values: readonly string[]): void =>
    update((next) => (values.length === 0 ? next.delete(key) : next.set(key, values.join(LIST_SEPARATOR))));
  /** Picking the charted metric changes nothing else, so the table keeps its page. */
  const setMetric = (next: PpcMetricKey): void =>
    setParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.set(PPC_PARAM.metric, next);
        return params;
      },
      { replace: true },
    );
  /** A new window opens on its own default interval; a picked one keeps both days in the URL. */
  const setWindow = (range: PpcPreset | PickMode, days?: { from: string; to: string }): void =>
    update((next) => {
      next.set(PPC_WINDOW_PARAM.range, range);
      next.delete(PPC_WINDOW_PARAM.interval);
      if (days === undefined) {
        next.delete(PPC_WINDOW_PARAM.from);
        next.delete(PPC_WINDOW_PARAM.to);
      } else {
        next.set(PPC_WINDOW_PARAM.from, days.from);
        next.set(PPC_WINDOW_PARAM.to, days.to);
      }
    });

  const query = { from: span.from, to: span.to, interval: span.interval, compare, split, campaignIds, groupKeys };
  const analytics = useQuery({
    queryKey: ['ppc-analytics', query],
    queryFn: ({ signal }) => readPpcAnalytics(query, signal),
    // Keep the last answer on screen while a filter change loads, so the page does not blink.
    placeholderData: keepPreviousData,
  });
  const data = analytics.data;

  /** Picking campaigns drops chosen groups that belong to campaigns no longer picked. */
  function chooseCampaigns(next: readonly string[]): void {
    const keep = new Set(next);
    const groupCampaign = new Map((data?.options.groups ?? []).map((group) => [group.key, group.campaignId]));
    const groups = next.length === 0 ? groupKeys : groupKeys.filter((key) => keep.has(groupCampaign.get(key) ?? ''));
    update((params) => {
      if (next.length === 0) params.delete(PPC_PARAM.campaigns);
      else params.set(PPC_PARAM.campaigns, next.join(LIST_SEPARATOR));
      if (groups.length === 0) params.delete(PPC_PARAM.groups);
      else params.set(PPC_PARAM.groups, groups.join(LIST_SEPARATOR));
    });
  }

  const filtered = campaignIds.length > 0 || groupKeys.length > 0;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="PPC"
        subtitle="Google Ads spend, traffic and what it turned into: leads, proposals and booked revenue. Filter to campaigns or ad groups, compare with an earlier period, and split the chart to watch one group over time."
      />

      <div className="mb-5 space-y-3">
        <PpcRangeControl
          selection={selection}
          span={span}
          today={today}
          onPreset={(preset) => setWindow(preset)}
          onPick={(mode, from, to) => setWindow(mode, { from, to })}
          onInterval={(next) => setParam(PPC_WINDOW_PARAM.interval, next)}
        />
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="min-w-0">
            <span className="mb-1.5 block text-portal-tiny font-bold">Compare with</span>
            <SegmentedControl<PpcCompare>
              label="Compare with"
              value={compare}
              onChange={(next) => setParam(PPC_PARAM.compare, next)}
              options={PPC_COMPARES.map((key) => ({
                key,
                label: COMPARE_LABELS[key],
                shortLabel: COMPARE_SHORT_LABELS[key],
                disabled: !span.compare && key !== PPC_COMPARE.none,
                disabledReason: 'All time has no earlier period',
              }))}
            />
          </div>
          <div className="min-w-0">
            <span className="mb-1.5 block text-portal-tiny font-bold">Split chart by</span>
            <SegmentedControl<PpcSplit>
              label="Split chart by"
              value={split}
              onChange={(next) => setParam(PPC_PARAM.split, next)}
              options={PPC_SPLITS.map((key) => ({ key, label: SPLIT_LABELS[key] }))}
            />
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Campaigns" htmlFor="ppc-campaigns" hint={campaignIds.length === 0 ? 'Every campaign' : undefined}>
            <MultiCombobox
              id="ppc-campaigns"
              options={(data?.options.campaigns ?? []).map((campaign) => ({ value: campaign.id, label: campaign.name }))}
              values={campaignIds}
              onChange={chooseCampaigns}
              placeholder="All campaigns"
            />
          </Field>
          <Field
            label="Ad groups and asset groups"
            htmlFor="ppc-groups"
            hint={groupKeys.length === 0 ? (campaignIds.length === 0 ? 'Every group' : 'Every group in the chosen campaigns') : undefined}
          >
            <MultiCombobox
              id="ppc-groups"
              options={(data?.options.groups ?? []).map((group) => ({
                value: group.key,
                label: `${group.name} · ${group.campaignName}${group.kind === PPC_ENTITY_KIND.assetGroup ? ' (asset group)' : ''}`,
              }))}
              values={groupKeys}
              onChange={(next) => setList(PPC_PARAM.groups, next)}
              placeholder="All ad groups"
            />
          </Field>
        </div>
        <p className="text-portal-micro text-portal-muted">
          <span className="font-bold text-portal-ink">{windowLabel(span)}</span>
          {data?.compare ? <> · compared with {windowLabel(data.compare)}</> : null}
          {data?.latestDate ? <> · Google Ads data through {data.latestDate}, synced hourly; the last two days are still settling</> : null}
        </p>
        {span.interval === STATS_INTERVAL.day ? <p className="text-portal-micro text-portal-muted">{DAILY_RATES_NOTE}</p> : null}
      </div>

      {analytics.isError ? (
        <ErrorNotice title="PPC analytics unavailable" message={errorMessage(analytics.error)} />
      ) : data === undefined ? (
        <PpcSkeleton />
      ) : data.rows.length === 0 && data.totals.spend === 0 && data.totals.leads === 0 ? (
        <Panel>
          <EmptyState
            title="No Google Ads activity for these filters"
            message="Nothing was spent and no paid lead arrived in this range. Try a longer range, or clear the campaign and ad group filters."
            action={
              filtered ? (
                <Button variant="secondary" onClick={() => update((next) => { next.delete(PPC_PARAM.campaigns); next.delete(PPC_PARAM.groups); })}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </Panel>
      ) : (
        <PpcBody data={data} interval={span.interval} metric={metric} onMetric={(next) => setMetric(next)} />
      )}
    </section>
  );
}

function PpcSkeleton(): React.ReactElement {
  return (
    <div role="status" aria-label="Loading PPC analytics" className="space-y-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {PPC_METRICS.map((metric) => (
          <Skeleton key={metric} className="h-[104px] rounded-portal-card" />
        ))}
      </div>
      <Skeleton className="h-[460px] rounded-portal-card" />
      <SkeletonTable rows={6} label="Loading the campaign table" />
    </div>
  );
}

function PpcBody({
  data,
  interval,
  metric,
  onMetric,
}: {
  readonly data: PpcAnalytics;
  readonly interval: StatsInterval;
  readonly metric: PpcMetricKey;
  readonly onMetric: (metric: PpcMetricKey) => void;
}): React.ReactElement {
  const previous = data.compareTotals;
  const compareNote = data.compare ? COMPARE_LABELS[data.compare.mode].toLowerCase() : null;
  return (
    <>
      <p className="mb-2 text-portal-micro text-portal-muted">Pick a tile to chart it.</p>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {PPC_METRICS.map((key) => {
          const def = PPC_METRIC_DEFS[key];
          const value = data.totals[key];
          const before = previous?.[key] ?? null;
          return (
            <StatTile
              key={key}
              label={def.label}
              labelAccessory={<InfoTip label={def.label.toLowerCase()} text={def.info} />}
              value={formatMetric(key, value)}
              changePct={previous === null ? null : changePct(value, before)}
              downIsGood={def.downIsGood}
              onSelect={() => onMetric(key)}
              selected={key === metric}
              {...(compareNote !== null ? { hint: `${formatMetric(key, before)} ${compareNote}` } : {})}
            />
          );
        })}
      </div>

      <MetricChart data={data} metric={metric} interval={interval} onMetric={onMetric} />

      <EntityTable data={data} />
      <CoverageNote data={data} />
      <ChangesPanel changes={data.changes} />
    </>
  );
}

function chartDescription(data: PpcAnalytics): string {
  if (data.splitSeries !== null) {
    return `One line per ${data.split === PPC_SPLIT.group ? 'ad group' : 'campaign'}: the six with the most spend, the rest as Other. Comparison lines show when the chart is not split.`;
  }
  return data.compare
    ? `The dashed line is the ${COMPARE_LABELS[data.compare.mode].toLowerCase()}, lined up period by period.`
    : 'Pick a comparison above to draw an earlier period alongside.';
}

function MetricChart({
  data,
  metric,
  interval,
  onMetric,
}: {
  readonly data: PpcAnalytics;
  readonly metric: PpcMetricKey;
  readonly interval: StatsInterval;
  readonly onMetric: (metric: PpcMetricKey) => void;
}): React.ReactElement {
  const def = PPC_METRIC_DEFS[metric];
  const lines = splitLines(data, metric);
  return (
    <Panel className="mt-6 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Heading variant="card" as="h2">
            {def.label} by {interval}
          </Heading>
          <p className="text-portal-micro text-portal-muted">{chartDescription(data)}</p>
        </div>
        <Field label="Chart" htmlFor="ppc-metric" className="w-64">
          <SelectInput
            id="ppc-metric"
            value={metric}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              const next = event.target.value;
              if (isMetric(next)) onMetric(next);
            }}
          >
            {PPC_METRIC_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.metrics.map((key) => (
                  <option key={key} value={key}>
                    {PPC_METRIC_DEFS[key].label}
                  </option>
                ))}
              </optgroup>
            ))}
          </SelectInput>
        </Field>
      </div>
      <TrendChart
        className="mt-3"
        points={chartPoints(data, metric)}
        {...(lines !== undefined ? { series: lines } : { overlays: compareOverlay(data, metric) })}
        title={`${def.label} over time`}
        unit={def.label}
        variant="line"
        height={CHART_HEIGHT}
        xAxisLabelInterval={labelInterval(data.periods.length)}
        formatValue={def.axis}
      />
    </Panel>
  );
}

function ChangesPanel({ changes }: { readonly changes: PpcAnalytics['changes'] }): React.ReactElement {
  return (
    <Panel className="mt-6 p-5">
      <Heading variant="card" as="h2">
        Account changes
      </Heading>
      <p className="text-portal-micro text-portal-muted">Logged in the PPC runbook in this range, to read a jump in the chart against what changed.</p>
      {changes.length === 0 ? (
        <div className="mt-2">
          <EmptyState title="No logged changes" message="Changes made in this range show here once the runbook's change log is synced." />
        </div>
      ) : (
        <ol className="mt-3 grid gap-x-8 gap-y-2 md:grid-cols-2 xl:grid-cols-3">
          {[...changes].reverse().map((change) => (
            <li key={`${change.date}|${change.title}`} className="grid grid-cols-[5.5rem_1fr] gap-3 text-portal-small">
              <span className="text-portal-muted tabular-nums">{change.date}</span>
              <span className="text-portal-ink">{change.title}</span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

type FlatRow = PpcMetrics & { readonly key: string; readonly label: string; readonly kind: PpcEntityKind; readonly campaignName: string };

function EntityTable({ data }: { readonly data: PpcAnalytics }): React.ReactElement {
  const { sort, onSort } = useTableSort(TABLE_SORT_KEYS, DEFAULT_SORT);
  const search = useUrlSearch();
  const flat: FlatRow[] = useMemo(
    () => data.rows.map((row) => ({ ...row.totals, key: row.key, label: row.label, kind: row.kind, campaignName: row.campaignName })),
    [data.rows],
  );
  const rows = useMemo(() => sortRows(filterRows(flat, search.search, SEARCH_FIELDS), sort), [flat, search.search, sort]);
  const paged = usePagedRows(rows, { pageKey: TABLE_PAGE_PARAM });
  const byGroup = data.split === PPC_SPLIT.group;
  const subtitle = (row: FlatRow): string => (row.campaignName === '' ? ROW_KIND_LABELS[row.kind] : `${row.campaignName} · ${ROW_KIND_LABELS[row.kind]}`);
  const cards: DataTableCard[] = paged.pageRows.map((row) => ({
    key: row.key,
    title: row.label,
    subtitle: subtitle(row),
    fields: CARD_METRICS.map((metric) => ({ label: PPC_METRIC_DEFS[metric].shortLabel, value: formatMetric(metric, row[metric]) })),
  }));

  return (
    <div className="mt-8">
      <SectionHeader
        title={byGroup ? 'By ad group' : 'By campaign'}
        description="Totals for the range. Sort any column; split the chart by ad group to list ad groups and asset groups here."
      />
      <div className="mb-3">
        <SearchInput
          label={byGroup ? 'Search ad groups' : 'Search campaigns'}
          placeholder={byGroup ? 'Ad group or campaign' : 'Campaign name'}
          value={search.search}
          onChange={(event) => search.setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>
      {rows.length === 0 ? (
        <Panel>
          <EmptyState title="Nothing matches" message="Try a different search." />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: byGroup ? 'Ad group' : 'Campaign', sortKey: 'label' },
              ...PPC_METRICS.map((metric) => ({ label: PPC_METRIC_DEFS[metric].shortLabel, sortKey: metric })),
            ]}
            rightAlignedHeaders={PPC_METRICS.map((_, index) => index + 1)}
            sort={sort}
            onSort={onSort}
            cards={cards}
          >
            {paged.pageRows.map((row) => (
              <tr key={row.key} className="border-t border-portal-line align-middle">
                <td className="min-w-60 p-3 text-portal-small">
                  <div className="font-bold">{row.label}</div>
                  <div className="text-portal-micro text-portal-muted">{subtitle(row)}</div>
                </td>
                {PPC_METRICS.map((metric) => (
                  <td
                    key={metric}
                    className={`p-3 text-right text-portal-small whitespace-nowrap${metric === PPC_METRIC.spend || metric === PPC_METRIC.costPerLead ? ' font-bold' : ''}${row[metric] === null ? ' text-portal-muted' : ''}`}
                  >
                    {row[metric] === null ? NO_VALUE : formatMetric(metric, row[metric])}
                  </td>
                ))}
              </tr>
            ))}
          </DataTable>
          <Pagination page={paged.page} totalPages={paged.totalPages} onChange={paged.setPage} summary={paged.summary} className="mt-3" />
        </>
      )}
    </div>
  );
}

function CoverageNote({ data }: { readonly data: PpcAnalytics }): React.ReactElement {
  const { leads, matchedLeads, groupDataFrom } = data.coverage;
  return (
    <div className="mt-3 space-y-1 text-portal-micro text-portal-muted">
      <p>
        {matchedLeads.toLocaleString('en-US')} of {leads.toLocaleString('en-US')} counted paid leads in this range are tied to a campaign through their Google click.
        Google keeps clicks for 90 days, so older leads count in the totals but in no campaign. Proposals and booked land in the period the lead arrived, so the latest periods fill in as deals move.
      </p>
      {data.split === PPC_SPLIT.group ? (
        <p>
          Ad group and asset group figures start {groupDataFrom ?? 'once the first group rows sync'}{groupDataFrom === null ? '' : ' in this range'}. Google reports no asset group for a
          Performance Max click, so those leads sit on their campaign's "without an ad group" row.
        </p>
      ) : null}
    </div>
  );
}
